import {
    CodedProperty,
    defaultCodedProperty,
    defaultNamedProperty,
    NamedProperty,
    ResolutionProperty
} from "sprint-common-web";

import {
    CloudOrderMetadata,
    GetOrderDetailsResponseItem
} from "../../../common/api/orders-api/order/GetOrderDetailsResponse";
import { NoteItem } from "../../../common/components/notes/hooks/state/NoteItem";
import { ContentReferenceType } from "../../../common/types/ContentReferenceType";
import { ContentSubtype } from "../../../common/types/ContentSubtype";
import { DynamicSubtitleType } from "../../../common/types/DynamicSubtitleType";
import { OrderContentReference } from "../../../common/types/OrderContentReference";
import { OrderAssetAvailability } from "../../common/types/OrderAssetAvailability";
import { OrderAssetStorageType } from "../../common/types/OrderAssetStorageType";
import { OrderResolutionDimensions } from "../../common/types/OrderResolutionDimensions";
import { mapToOrderStatus, OrderStatus } from "../../common/types/OrderStatus";
import { OrderVideoSelectionSource } from "../../common/types/OrderVideoSelectionSource";
import { CloudOrderSource } from "../listing/OrderListingItem";

export enum VideoFileType {
    H265 = 1,
    H265Trimmed = 2
}

export enum AudioFileType {
    Loudnorm = 1,
    LoudnormTrimmed = 2
}

export type LanguageAssetType = AudioFileType | DynamicSubtitleType;

export type OrderAssetType = VideoFileType | AudioFileType | DynamicSubtitleType;

export interface OrderDashboardItem {
    id: string;
    cloudAssets: CloudAssetsDashboardItem;
    pONumber: string;
    content: OrderContentReference;
    airline: CodedProperty;
    system: NamedProperty;
    integrator: NamedProperty;
    encodeSpecification: NamedProperty & { targetAspectRatio: NamedProperty };
    targetResolution?: OrderResolutionDimensions;
    deliveryDate?: string;
    dueDate: string;
    targetFileName: string;
    deliveryFolder: string | undefined;
    startDate: string;
    deliveryEndpoint: NamedProperty;
    materialSpecification?: MaterialSpecification;
    status: OrderStatus;
    bucket: string;
    source: CloudOrderSource;
    overridenById?: string;
    notes?: string;
    buzzId?: string;
    isTrailer?: boolean;
    muxSubtitleType?: DynamicSubtitleType;
    copySubtitleType?: DynamicSubtitleType;
    assetNotes: NoteItem[];
    metadata?: CloudOrderMetadata;
}

interface CloudAssetsDashboardItem {
    video: CloudOrderVideoDashboardItem;
    soundtracks: CloudOrderSoundtrackDashboardItem[];
    subtitles: CloudOrderSubtitleDashboardItem[];
}

interface CloudOrderVideoDashboardItem {
    targetFilename: string;
    availability: OrderAssetAvailability;
    aspectRatio: NamedProperty;
    targetResolution?: ResolutionProperty;
    sourceResolution?: ResolutionProperty;
    subtitle?: CodedProperty;
    assetId?: string;
    storageType?: OrderAssetStorageType;
    filePath?: string;
    fileType?: number;
    bucket?: string;
    selectionSource?: OrderVideoSelectionSource;
    nonMatchingVideos: CloudAssetsNonMatchingVideoDashboardItem[];
}

interface CloudAssetsNonMatchingVideoDashboardItem {
    assetId: string;
    storageType?: OrderAssetStorageType;
    subtitle: CodedProperty;
    aspectRatio: NamedProperty;
    resolution: ResolutionProperty;
}

interface CloudOrderSoundtrackDashboardItem {
    language: CodedProperty;
    availability: OrderAssetAvailability;
    assetId?: string;
    storageType?: OrderAssetStorageType;
    filePath?: string;
    fileType?: number;
    bucket?: string;
}

export interface CloudOrderSubtitleDashboardItem {
    language: CodedProperty;
    subtitleType: DynamicSubtitleType;
    availability: OrderAssetAvailability;
    assetAvailability: OrderAssetAvailability;
    assetId?: string;
    storageType?: OrderAssetStorageType;
    targetFilename?: string;
    isEmbedded: boolean;
    filePath?: string;
    bucket?: string;
}

interface MaterialSpecification {
    encodingFormat?: string;
    aspectRatio?: string;
    bitRate?: string;
    resolution?: string;
    runtime?: string;
    notes?: string;
}

export const defaultOrderDashboardItem: OrderDashboardItem = {
    id: "",
    airline: defaultCodedProperty,
    cloudAssets: {
        video: {
            targetFilename: "",
            availability: OrderAssetAvailability.NoMatch,
            aspectRatio: defaultNamedProperty,
            nonMatchingVideos: [],
            selectionSource: undefined,
            bucket: ""
        },
        soundtracks: [],
        subtitles: []
    },
    content: {
        type: ContentReferenceType.Movie,
        movie: { id: "", title: "" },
        subtype: ContentSubtype.Movie,
        version: { id: "", type: defaultNamedProperty, distributor: defaultNamedProperty }
    },
    deliveryDate: "",
    targetFileName: "",
    deliveryFolder: "",
    startDate: "",
    deliveryEndpoint: defaultNamedProperty,
    encodeSpecification: { ...defaultNamedProperty, targetAspectRatio: defaultNamedProperty },
    integrator: defaultNamedProperty,
    pONumber: "",
    system: defaultNamedProperty,
    status: OrderStatus.Pending,
    source: CloudOrderSource.Sprint,
    materialSpecification: {},
    bucket: "",
    overridenById: "",
    dueDate: "",
    notes: "",
    isTrailer: false,
    assetNotes: []
};

export const mapToOrderDashboardItem = (order: GetOrderDetailsResponseItem): OrderDashboardItem => {
    return {
        id: order.id,
        content: order.content,
        airline: order.airline,
        system: order.system,
        dueDate: new Date(order.deliveryInfo.dueDate).toString(),
        deliveryDate: order.deliveryInfo.deliveryDate
            ? new Date(order.deliveryInfo.deliveryDate).toString()
            : undefined,
        targetFileName: order.targetFileName,
        targetResolution: order.targetResolution,
        deliveryFolder: order.deliveryInfo.deliveryFolder,
        startDate: new Date(order.startDate).toString(),
        deliveryEndpoint: order.deliveryInfo.deliveryEndpoint,
        cloudAssets: {
            video: {
                targetFilename: order.cloudAssets.video.targetFilename,
                availability: order.cloudAssets.video.availability,
                aspectRatio: order.cloudAssets.video.aspectRatio,
                targetResolution: order.cloudAssets.video.targetResolution,
                sourceResolution: order.cloudAssets.video.sourceResolution,
                subtitle: order.cloudAssets.video.subtitle,
                assetId: order.cloudAssets.video.assetId,
                storageType: order.cloudAssets.video.storageType,
                filePath: order.cloudAssets.video.filePath,
                fileType: order.cloudAssets.video.fileType,
                bucket: order.bucket,
                nonMatchingVideos: order.cloudAssets.video.nonMatchingVideos.map((item) => ({
                    assetId: item.assetId,
                    subtitle: item.subtitle,
                    aspectRatio: item.aspectRatio,
                    resolution: item.resolution
                }))
            },
            soundtracks: order.cloudAssets.soundtracks.map((soundtrack) => ({
                availability: soundtrack.availability,
                language: soundtrack.language,
                assetId: soundtrack.assetId,
                storageType: soundtrack.storageType,
                filePath: soundtrack.filePath,
                fileType: soundtrack.fileType,
                bucket: soundtrack.bucket
            })),
            subtitles: order.cloudAssets.subtitles.map((subtitle) => ({
                availability: subtitle.availability,
                assetAvailability: subtitle.assetAvailability,
                isEmbedded: subtitle.isEmbedded,
                language: subtitle.language,
                subtitleType: subtitle.subtitleType,
                assetId: subtitle.assetId,
                storageType: subtitle.storageType,
                targetFilename: subtitle.targetFilename,
                filePath: subtitle.filePath,
                bucket: subtitle.bucket
            }))
        },
        encodeSpecification: order.encodeSpecification,
        materialSpecification: order.materialSpecification,
        copySubtitleType: order.copySubtitleType,
        muxSubtitleType: order.muxSubtitleType,
        integrator: order.integrator,
        pONumber: order.pONumber,
        status: mapToOrderStatus(order.status),
        source: order.source,
        bucket: order.bucket,
        overridenById: order.overridenById,
        notes: order.notes,
        metadata: order.metadata,
        buzzId: order.buzzId,
        isTrailer: order.isTrailer,
        assetNotes: []
    };
};
